/**
 * Pure presentation helpers for the company Support Inbox tab.
 *
 * Kept out of the display component so the formatting rules (dates, sender
 * headers, avatar initials/tones) have one home the thread list, the reading
 * pane and the ticket-description builder all read from.
 */

import { hasVisibleAttachments } from "./attachment-visibility";
import type {
  SupportInboxListMessage,
  SupportInboxThreadSummary,
} from "./types";

export function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Format the sender header ("Jane <jane@x>") for display, preferring the name. */
export function displayFrom(from: string): string {
  const angle = from.match(/^(.*)<[^>]+>\s*$/);
  if (angle && angle[1].trim()) {
    return angle[1].trim().replace(/^"(.*)"$/, "$1").trim();
  }
  return from.trim();
}

/** Two-letter avatar initials from a name or email, always uppercase. */
export function initialsOf(label: string): string {
  const trimmed = (label ?? "").trim();
  if (!trimmed) return "?";
  const namey = trimmed.replace(/<[^>]*>/g, "").trim() || trimmed;
  const parts = namey.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return namey.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic muted background for an avatar, keyed on the label. */
export function avatarTone(label: string): string {
  const tones = [
    "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
    "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length];
}

/** Whether any message in a thread carries a listable attachment. */
export function threadHasAttachments(thread: SupportInboxThreadSummary): boolean {
  return thread.messages.some((message) => hasVisibleAttachments(message.attachments));
}

/** Plain-text ticket description built from a support email. */
export function buildTicketDescription(msg: SupportInboxListMessage): string {
  return [
    `From: ${msg.from}`,
    `Received: ${formatWhen(msg.receivedAt)}`,
    "",
    msg.snippet || "(No preview available — see the support inbox for the full email.)",
  ].join("\n");
}
